import React, { useState } from 'react';
import { ShoppingCart, CreditCard, Package, Repeat, Sparkles, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const Ecommerce = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast({
      title: "Harika! 🛒",
      description: "E-ticaret ekibimiz 24 saat içinde sizinle iletişime geçecek.",
    });
    setFormData({ name: '', email: '', phone: '', projectType: '', budget: '', message: '' });
  };

  const features = [
    {
      icon: ShoppingCart,
      title: 'E-Ticaret Platformları',
      description: 'Shopify, WooCommerce ve özel e-ticaret altyapıları kurulumu'
    },
    {
      icon: CreditCard,
      title: 'Ödeme Entegrasyonları',
      description: 'Güvenli ödeme sistemleri, abonelik ve taksit altyapıları'
    },
    {
      icon: Package,
      title: 'Stok & Lojistik',
      description: 'Envanter yönetimi, kargo entegrasyonları ve sipariş takibi'
    },
    {
      icon: Repeat,
      title: 'Satış Otomasyonu',
      description: 'Kampanya yönetimi, sepet hatırlatma ve müşteri segmentasyonu'
    }
  ];

  const process = [
    { step: '01', title: 'İş Analizi', desc: 'Ürün portföyü ve hedef pazar analizi' },
    { step: '02', title: 'Platform Seçimi', desc: 'İhtiyaca uygun e-ticaret altyapısı' },
    { step: '03', title: 'Entegrasyonlar', desc: 'Ödeme, kargo ve ERP entegrasyonları' },
    { step: '04', title: 'Yayın & Büyüme', desc: 'Lansman ve satış optimizasyonu' }
  ];

  const technologies = ['Shopify', 'WooCommerce', 'Stripe', 'PayPal', 'iyzico', 'Trendyol', 'N11', 'Amazon'];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-indigo-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-indigo-400 animate-spin-slow" />
                <span className="text-indigo-400 text-sm font-medium">E-Ticaret & Satış Sistemleri</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Online
                <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent"> Satışlarınızı </span>
                Katlayın
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Dijital ürün ve servis satış altyapıları, abonelik sistemleri ve e-ticaret entegrasyonları ile gelir modellerinizi optimize edin.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white px-8 py-6 text-lg hover:scale-105 transition-all duration-300"
                  data-testid="service-cta-button"
                >
                  E-Ticaret Teklifi Al
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop"
                alt="E-commerce"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            E-Ticaret <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Çözümlerimiz</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-indigo-500/50 transition-all duration-500 hover:transform hover:translateY(-4px) hover:shadow-2xl hover:shadow-indigo-500/20"
                style={{
                  animation: 'fade-in-up 0.8s ease-out both',
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <div className="relative w-14 h-14 mb-4">
                  <div className="absolute inset-0 border-2 border-transparent border-t-indigo-500/60 border-r-blue-500/40 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-0 border-2 border-transparent border-t-blue-500/60 border-r-indigo-500/40 rounded-full animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse', transform: 'scale(0.8)' }}></div>
                  
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                    <feature.icon className="w-7 h-7 text-white transition-transform duration-500 group-hover:rotate-[-12deg]" />
                  </div>

                  <div className="absolute inset-0 bg-indigo-500/50 rounded-xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">E-Ticaret Süreci</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-indigo-500/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-indigo-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Entegre Ettiğimiz <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Platformlar</span>
          </h2>

          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div key={index} className="px-6 py-3 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl text-gray-300 hover:border-indigo-500/50 hover:text-indigo-400 hover:scale-110 transition-all duration-300">
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="contact-form" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-indigo-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-indigo-500/30 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                E-Ticaret Dönüşümünüzü
                <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent"> Başlatalım! </span>
              </h2>
              <p className="text-gray-400 text-lg">
                Ücretsiz e-ticaret danışmanlığı ve teklif için formu doldurun
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-indigo-500/30 text-white h-12 rounded-xl focus:border-indigo-500 focus:ring-indigo-500 transition-all"
                    placeholder="Ahmet Yılmaz"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-indigo-500/30 text-white h-12 rounded-xl focus:border-indigo-500 focus:ring-indigo-500 transition-all"
                    placeholder="ahmet@sirket.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 block">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-slate-900/50 border-indigo-500/30 text-white h-12 rounded-xl focus:border-indigo-500 focus:ring-indigo-500 transition-all"
                    placeholder="+90 XXX XXX XX XX"
                  />
                </div>
                <div>
                  <Label htmlFor="budget" className="text-gray-300 mb-2 block">Proje Bütçesi</Label>
                  <select
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full bg-slate-900/50 border border-indigo-500/30 text-white h-12 rounded-xl px-4 focus:border-indigo-500 focus:ring-indigo-500 transition-all"
                  >
                    <option value="">Seçiniz</option>
                    <option value="10k-25k">€10K - €25K</option>
                    <option value="25k-50k">€25K - €50K</option>
                    <option value="50k+">€50K+</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300 mb-2 block">Proje Detayları *</Label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-900/50 border-indigo-500/30 text-white rounded-xl resize-none focus:border-indigo-500 focus:ring-indigo-500 transition-all"
                  placeholder="Nasıl bir e-ticaret platformu düşünüyorsunuz? Kaç ürününüz var? Mevcut sisteminiz var mı?"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white py-6 text-lg font-semibold rounded-xl group hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/50"
              >
                Ücretsiz E-Ticaret Danışmanlığı
                <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-center text-sm text-gray-500">
                🛒 Tam Entegrasyon • 💎 Güvenli Ödeme • 🚀 Hızlı Kurulum
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ecommerce;
